/** A description longer than this is cut to a window around its first match. */
const SNIPPET_LENGTH = 140;

/** How much of the text before the match the window keeps. */
const LEAD = 30;

/**
 * The part of a workspace's description to show under its name in a search, or null when
 * the description holds none of the searched words.
 *
 * The workspace list search matches the name and the description
 * (`PaginationRepositoryHelper` in the API), but the table only shows the name, so a
 * workspace found through its description looked like a random hit. This mirrors that
 * search: the term splits into words at whitespace, and a word matches anywhere in the
 * text, ignoring case. Text is compared in composed Unicode (NFC), like `HighlightedText`.
 */
export const workspaceDescriptionMatch = (
  description: string | null | undefined,
  searchTerm: string | undefined
): string | null => {
  const text = (description ?? "").normalize("NFC").trim();
  const words = (searchTerm ?? "")
    .normalize("NFC")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!text || words.length === 0) return null;

  const lower = text.toLowerCase();
  const hits = words
    .map((word) => ({ index: lower.indexOf(word), length: word.length }))
    .filter((hit) => hit.index >= 0);
  if (hits.length === 0) return null;
  if (text.length <= SNIPPET_LENGTH) return text;

  const first = hits.reduce((a, b) => (b.index < a.index ? b : a));

  // Start a little before the match, at the beginning of a word.
  let start = Math.max(0, first.index - LEAD);
  if (start > 0) {
    const space = text.indexOf(" ", start);
    if (space !== -1 && space < first.index) start = space + 1;
  }

  // End at the end of a word, never before the match itself.
  let end = Math.min(text.length, start + SNIPPET_LENGTH);
  if (end < text.length) {
    const space = text.lastIndexOf(" ", end);
    if (space > first.index + first.length) end = space;
  }

  const snippet = text.slice(start, end).trim();
  return `${start > 0 ? "…" : ""}${snippet}${end < text.length ? "…" : ""}`;
};
